<template>
    <div class="fill-current stroke-current">
        <button
            class="fixed bottom-12 left-1/2 z-40 flex h-16 -translate-x-1/2 items-center rounded-full bg-gutex-rot px-1 text-gutex-rot shadow-floating transition-all hover:scale-105 hover:bg-gutex-rot-110 hover:text-gutex-rot-110 hover:shadow-floating"
            @click="toggle">
            <div class="flex h-14 w-14 items-center justify-center rounded-full bg-white">
                <svg-icon name="burger" />
            </div>
            <div class="text-button-lg pl-6 pr-14 text-white">{{ $t("elements.menu.title") }}</div>
        </button>
        <nav
            class="fixed left-0 top-0 z-50 h-dvh min-h-full w-screen overflow-auto bg-anthrazit-100 text-white sm:text-anthrazit-20"
            :class="active ? 'fade-in' : 'fade-out'">
            <div class="grid-container relative">
                <div class="grid-container--inner relative">
                    <div
                        class="gutter grid h-28 grid-cols-[fit-content(0)_1fr] items-center text-lg transition-all sm:my-12 sm:h-auto sm:grid-cols-12">
                        <div
                            class="flex transition-all sm:col-span-4 sm:w-auto lg:col-span-3 xl:col-span-2"
                            :class="curLevel > 1 ? 'w-8' : 'w-0 overflow-hidden'">
                            <a href="/">
                                <svg-icon name="gutex-logo-navi" class="hidden sm:block" auto />
                            </a>
                            <button
                                class="text-white transition-all sm:hidden"
                                :class="curLevel > 1 ? 'opacity-100' : 'opacity-0'"
                                @click="mobileLevelBack">
                                <svg-icon name="chevron-right" class="rotate-180" />
                            </button>
                        </div>
                        <div class="sm:col-span-8 sm:flex sm:justify-end lg:col-span-9 xl:col-span-10">
                            <slot name="search" />
                        </div>
                    </div>
                    <!-- Autocomplete result -->
                    <div class="XXbg-colorize-fancy mx-auto w-full overflow-hidden">
                        <div id="autocomplete-result-container" class="absolute w-full" />
                    </div>
                    <div
                        ref="swiper"
                        class="XXbg-colorize-fancy mx-auto overflow-hidden transition-all"
                        :class="{
                            'sm:w-[calc(100%/3-9px)]': curLevel === 1,
                            'w-full sm:w-[calc(100%/1.5+30px)]': curLevel >= 2,
                        }">
                        <div
                            class="transition-all"
                            :class="{
                                'w-[calc(300%+96px)]': curLevel === 1,
                                'w-[calc(300%+96px)] sm:w-[calc(150%+24px)]': curLevel >= 2,
                                'translate-x-[calc(-33%-20px)] sm:translate-x-0': curLevel === 2,
                                'translate-x-[calc(-66%-41px)] sm:translate-x-[calc(-33%-20px)]': curLevel === 3,
                            }"
                            @transitionend="removeNaviSliderWidth">
                            <div ref="naviSlider" class="grid grid-cols-3 gap-x-12">
                                <!-- level 1 -->
                                <div class="h-[calc(100vh-112px-136px)] overflow-auto sm:h-[calc(100vh-150px-150px)]">
                                    <!-- level 1 main -->
                                    <div class="mb-6 grid grid-cols-1 gap-y-2 sm:mt-10">
                                        <template v-for="(page, idx) in navigation" :key="idx">
                                            <component
                                                :is="page.pages.length == 0 && page.uri ? 'a' : 'button'"
                                                :href="page.uri"
                                                class="grid grid-cols-[1fr_1.5rem] items-center border-b border-transparent py-2 text-left hover:text-white sm:py-4 sm:hover:border-white"
                                                :class="{ 'sm:!border-white sm:!text-white': page.id === activeItem1 }"
                                                @click="page.pages.length == 0 ? null : setActiveLevel(2, idx, page.id)">
                                                <div class="text-menulink-md flex min-h-icon-size-md items-center pr-4">
                                                    <span>{{ page.label }}</span>
                                                </div>
                                                <svg-icon v-if="page.pages.length > 0" name="chevron-right" />
                                            </component>
                                        </template>
                                    </div>

                                    <!-- meta navi -->
                                    <div class="mb-8 mt-4 grid grid-cols-1 gap-y-4">
                                        <slot name="metanavi" />
                                    </div>
                                </div>

                                <!-- level 2 -->
                                <div class="h-[calc(100vh-112px-136px)] overflow-auto sm:h-[calc(100vh-150px-150px)]">
                                    <template v-for="(pageLevel1, idx1) in navigation" :key="idx1">
                                        <template v-if="pageLevel1.pages">
                                            <div v-show="activeLevel2 === idx1" class="mb-6 grid grid-cols-1">
                                                <!-- headline from level 1 - only on mobile -->
                                                <div class="mb-2 text-xl font-bold text-anthrazit-40 sm:hidden">{{ pageLevel1.label }}</div>

                                                <!-- menu block L2 -->
                                                <template v-for="(pageLevel2, idx2) in pageLevel1.pages" :key="idx2">
                                                    <!-- back button - back-button only is there when first item is not a subhead -->
                                                    <div v-if="idx2 === 0 && pageLevel2.class != 'divider'" class="hidden h-10 sm:block">
                                                        <transition name="fade">
                                                            <button
                                                                v-show="curLevel === 3"
                                                                class="grid grid-cols-[2rem_1fr] items-center py-1.5 text-left hover:text-white"
                                                                @click="level2Back">
                                                                <svg-icon name="chevron-right" class="rotate-180" />
                                                                <div class="text-body-sm pr-4">Zurück</div>
                                                            </button>
                                                        </transition>
                                                    </div>

                                                    <!-- special layout when first item is a category headline -->
                                                    <div
                                                        v-if="idx2 === 0 && pageLevel2.class == 'divider'"
                                                        class="mb-3 grid h-10 grid-cols-[1fr_1.5rem] items-center py-1.5 text-left sm:mb-0">
                                                        <div class="text-category-head pr-4 text-anthrazit-40">{{ pageLevel2.label }}</div>
                                                    </div>

                                                    <!-- regular menu item -->
                                                    <component
                                                        :is="pageLevel2.pages.length == 0 && pageLevel2.uri ? 'a' : 'button'"
                                                        v-if="pageLevel2.class != 'divider'"
                                                        :href="pageLevel2.uri"
                                                        class="mb-2 grid grid-cols-[1fr_1.5rem] items-center border-b border-transparent py-2 text-left hover:text-white sm:py-4 sm:hover:border-white"
                                                        :class="{ 'sm:!border-white sm:!text-white': pageLevel2.id === activeItem2 }"
                                                        @click="
                                                            pageLevel2.pages.length == 0 ? null : setActiveLevel(3, idx2, pageLevel2.id)
                                                        ">
                                                        <div class="text-menulink-md flex min-h-icon-size-md items-center pr-4">
                                                            <span>{{ pageLevel2.label }}</span>
                                                        </div>
                                                        <svg-icon v-if="pageLevel2.pages.length > 0" name="chevron-right" />
                                                    </component>

                                                    <!-- subhead between menu items -->
                                                    <div
                                                        v-if="idx2 > 0 && pageLevel2.class == 'divider'"
                                                        class="mb-3 mt-4 grid grid-cols-[1fr_1.5rem] items-center py-1.5 text-left sm:mb-0 sm:mt-8">
                                                        <div class="text-category-head pr-4 text-anthrazit-40">{{ pageLevel2.label }}</div>
                                                    </div>
                                                </template>
                                            </div>
                                        </template>
                                    </template>
                                </div>

                                <!-- level 3 -->
                                <div class="h-[calc(100vh-112px-136px)] overflow-auto sm:h-[calc(100vh-150px-150px)]">
                                    <template v-for="(pageLevel1, idx1) in navigation" :key="idx1">
                                        <!-- headline from level 1 - only on mobile -->
                                        <div v-if="activeLevel2 === idx1" class="mb-2 text-xl font-bold text-anthrazit-40 sm:hidden">
                                            {{ pageLevel1.label }}
                                        </div>

                                        <template v-if="pageLevel1.pages">
                                            <template v-for="(pageLevel2, idx2) in pageLevel1.pages" :key="idx2">
                                                <template v-if="pageLevel2.pages">
                                                    <div
                                                        v-show="activeLevel2 === idx1 && activeLevel3 === idx2"
                                                        class="mb-6 grid grid-cols-1">
                                                        <template v-for="(pageLevel3, idx3) in pageLevel2.pages" :key="idx3">
                                                            <!-- special layout when first item is a category headline -->
                                                            <div
                                                                v-if="idx3 === 0"
                                                                class="mb-3 grid h-10 grid-cols-[1fr_1.5rem] items-center py-1.5 text-left hover:text-white sm:mb-0">
                                                                <div class="text-category-head pr-4 text-anthrazit-40">
                                                                    {{ pageLevel2.label }}
                                                                </div>
                                                            </div>

                                                            <!-- regular menu item -->
                                                            <component
                                                                :is="'a'"
                                                                v-if="pageLevel3.class != 'divider'"
                                                                :href="pageLevel3.uri"
                                                                class="mb-2 grid grid-cols-[1fr_1.5rem] items-center border-b border-transparent py-2 text-left hover:text-white sm:py-4 sm:hover:border-white">
                                                                <div class="text-menulink-md flex min-h-icon-size-md items-center pr-4">
                                                                    <span>{{ pageLevel3.label }}</span>
                                                                </div>
                                                            </component>
                                                        </template>
                                                    </div>
                                                </template>
                                            </template>
                                        </template>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- umständlicher mit grid
                    <div
                        class="grid XCgutter XXgap-y-4 mb-4 transition-all h-[300px]"
                        :class="{
                            'grid-cols-[1fr_16px_1fr_16px_1fr]': curLevel === 1,
                            'grid-cols-[1fr_16px_4.25fr_16px_1fr]': curLevel >= 2,
                        }">
                        <div class="bg-gutex-rot h-full" />
                        <div />
                        <div class="bg-sand-10">
                            <div
                                class="bg-amber-500 transition-all duration-[1000ms]"
                                :class="{
                                    'w-[calc(300%+30px)]': curLevel === 1,
                                    'w-[calc(150%+10px)]': curLevel >= 2,
                                }">
                                <div class="grid grid-cols-12 gutter">
                                    <div class="bg-lime-400">1/12</div>
                                    <div class="bg-lime-400">2/12</div>
                                    <div class="bg-lime-400">3/12</div>
                                    <div class="bg-lime-400">4/12</div>
                                    <div class="bg-lime-400">5/12</div>
                                    <div class="bg-lime-400">6/12</div>
                                    <div class="bg-lime-400">7/12</div>
                                    <div class="bg-lime-400">8/12</div>
                                    <div class="bg-lime-400">9/12</div>
                                    <div class="bg-lime-400">10/12</div>
                                    <div class="bg-lime-400">11/12</div>
                                    <div class="bg-lime-400">12/12</div>
                                </div>
                            </div>
                        </div>
                        <div />
                        <div class="bg-gutex-rot" />
                    </div>
                    -->

                    <!-- <div class="flex gap-x-4">
                        <svg-icon name="arrow-right" class="text-gutex-rot rotate-180" @click="levelTest(-1)" />
                        <div>Toggle level ({{ curLevel }})</div>
                        <svg-icon name="arrow-right" class="text-gutex-rot" @click="levelTest(1)" />
                    </div> -->
                </div>
            </div>
            <div class="absolute bottom-12 w-full">
                <button
                    title="Close"
                    class="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white stroke-current text-black transition-all hover:scale-105 hover:bg-anthrazit-10"
                    @click="close">
                    <svg-icon name="x" />
                </button>
            </div>
        </nav>
    </div>
</template>

<script setup lang="ts">
import SvgIcon from "@components/SvgIcon.vue"
import { useSwipe } from "@vueuse/core"

defineProps<{ navigation: Array<object> }>()
const active = ref(false)
const curLevel = ref(1)
const activeLevel2 = ref(-1)
const activeLevel3 = ref(-1)
const activeItem1 = ref("")
const activeItem2 = ref("")

// const navi = ref<HTMLDivElement>(null)
const naviSlider = ref<HTMLDivElement>(null)

const swiper = ref(null)
const { isSwiping, direction } = useSwipe(swiper)

function close() {
    active.value = false
    setTimeout(() => resetState(), 300)
}

function open() {
    resetState()
    active.value = true
}

function toggle() {
    resetState()
    active.value = !active.value
}

function resetState() {
    curLevel.value = 1
    activeLevel2.value = -1
    activeLevel3.value = -1
    activeItem1.value = ""
    activeItem2.value = ""
}

function setActiveLevel(level, idx, id) {
    console.debug("setActiveLevel: " + level + " :: " + idx)
    if (level === 2) {
        activeLevel2.value = idx
        activeItem1.value = id
        activeItem2.value = ""
    }
    if (level === 3) {
        activeLevel3.value = idx
        activeItem2.value = id
    }

    // moves the navi columns
    fixNaviSliderWidth()
    curLevel.value = level
}

function level2Back() {
    fixNaviSliderWidth()
    curLevel.value = 2
}

function mobileLevelBack() {
    if (curLevel.value > 1) {
        curLevel.value--
    }
}

/*
function fixTextColWidths() {
    const menuTexts: NodeList = navi.value.querySelectorAll(".menu-text")
    // console.log("menuTexts: ", menuTexts)
    menuTexts.forEach(el => {
        // console.log("w: ", el.clientWidth)
        el.setAttribute("style", "width: " + el.clientWidth + "px")
    })
}

function removeTextColWidths() {
    const menuTexts: NodeList = navi.value.querySelectorAll(".menu-text")
    // console.log("removeTextColWidths: ", menuTexts)
    menuTexts.forEach(el => {
        el.removeAttribute("style")
    })
}
 */

function fixNaviSliderWidth() {
    naviSlider.value.setAttribute("style", "width: " + naviSlider.value.clientWidth + "px")
}

function removeNaviSliderWidth() {
    naviSlider.value.removeAttribute("style")
}

function levelTest(dir) {
    //fixTextColWidths()
    fixNaviSliderWidth()
    curLevel.value = curLevel.value + dir
    if (curLevel.value > 3) {
        curLevel.value = 1
    }
    if (curLevel.value < 1) {
        curLevel.value = 3
    }
}

watch(isSwiping, newVal => {
    if (newVal && direction.value === "right") {
        // console.log("someone swipes " + direction.value)
        mobileLevelBack()
    }
})
</script>

<style scoped>
.fade-out {
    visibility: hidden;
    opacity: 0;
    transition:
        visibility 0s linear 300ms,
        opacity 300ms;
}
.fade-in {
    visibility: visible;
    opacity: 1;
    transition:
        visibility 0s linear 0s,
        opacity 300ms;
}

.show-enter-active,
.show-leave-active {
    @apply transition-all;
}

.show-enter-from,
.show-leave-to {
    @apply max-h-0 p-0 opacity-0;
}

.fade-enter-active,
.fade-leave-active {
    @apply transition-opacity;
}

.fade-enter-from,
.fade-leave-to {
    @apply opacity-0;
}

.search-field {
    @apply h-14 w-full rounded-full border border-anthrazit-40 bg-anthrazit-80 bg-[20px_center] bg-no-repeat pl-12 text-white placeholder:text-anthrazit-40 focus:border-anthrazit-40 focus:outline-none focus:ring-0;
}

.search-field-icon {
    background-image: url("data:image/svg+xml,%3Csvg%20width%3D%2218%22%20height%3D%2218%22%20viewBox%3D%220%200%2018%2018%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M8.1111%201.75C4.59796%201.75%201.75%204.59796%201.75%208.1111C1.75%2011.6242%204.59796%2014.4722%208.1111%2014.4722C11.6242%2014.4722%2014.4722%2011.6242%2014.4722%208.1111C14.4722%204.59796%2011.6242%201.75%208.1111%201.75ZM0.25%208.1111C0.25%203.76953%203.76953%200.25%208.1111%200.25C12.4527%200.25%2015.9722%203.76953%2015.9722%208.1111C15.9722%2012.4527%2012.4527%2015.9722%208.1111%2015.9722C3.76953%2015.9722%200.25%2012.4527%200.25%208.1111Z%22%20fill%3D%22%23B3B1B1%22%2F%3E%3Cpath%20d%3D%22M17.0005%2017L13.1338%2013.1333%22%20stroke%3D%22%23B3B1B1%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E");
}
</style>
